import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { useMountedState } from 'react-use';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import kebabCase from 'lodash/kebabCase';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';

import { Logger } from '../../../infrastructure/logger';
import { GeocodingFeature } from '../../../types/mapbox-search-api-v1.types.ts';
import Button from '../../controls/button/Button';
import Error from '../../error/Error';
import Field from '../field/Field';

import './Form.scss';

export interface FormField {
  field: string;
  label?;
  defaultValue?;
  type;
  hint?;
  required?;
  getPrefillValue?;
  getValueFromData?: (item: GeocodingFeature) => any;
  getFormControlValue?;
  beforeChange?;
  getValueOnOuterInputChange?;
  checkChanged?;
  validate?;
  controlProps?: Record<string, unknown>;
}

export interface FormState {
  values: Record<string, unknown>;
  touchedFields: Record<string, boolean>;
}

export interface FormProps {
  className?: string;
  fields?;
  state?: FormState;
  initialValues?;
  outerInput?;
  formDisabled?: boolean;
  validate?;
  stepLabel?;
  onStateChange;
  onSubmit;
  onCancel?;
  submitLabel?;
  cancelLabel?;
  footerHint?;
}

const Form = ({
  className,
  fields = [],
  state = { values: {}, touchedFields: {} },
  initialValues = {},
  outerInput,
  formDisabled,
  validate,
  stepLabel,
  onStateChange,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  footerHint,
}: FormProps) => {
  const stateRef = useRef<FormState>();
  stateRef.current = state;

  const [innerValues, setInnerValues] = useState(initialValues);
  const [fieldsErrors, setFieldsErrors] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [formError, setFormError] = useState(null);
  const [innerTouchedFields, setInnerTouchedFields] = useState({});

  const isMounted = useMountedState();

  // TODO: fulfill form outer state if it's not present, but can be initialized
  const values = useMemo(() => {
    if (onStateChange) {
      return state?.values || initialValues;
    }

    return innerValues;
  }, [innerValues, onStateChange, state?.values, initialValues]);
  const valuesRef = useRef<Record<string, unknown>>();
  valuesRef.current = values;

  const touchedFields = useMemo(() => {
    if (onStateChange) {
      return state?.touchedFields || {};
    }

    return innerTouchedFields;
  }, [innerTouchedFields, state, onStateChange]);
  const touchedFieldsRef = useRef<Record<string, boolean>>();
  touchedFieldsRef.current = touchedFields;

  const handleValuesChanged = useCallback(
    (values) => {
      if (isMounted()) {
        setInnerValues(values);
      }

      if (onStateChange) {
        const newState = {
          values,
          touchedFields: stateRef.current?.touchedFields || {},
        };
        // preserve state update before render in case of sequential updates
        stateRef.current = newState;

        onStateChange(newState);
      }
    },
    [onStateChange, isMounted],
  );

  const handleTouchedChanged = useCallback(
    (touchedFields) => {
      setInnerTouchedFields(touchedFields);

      if (onStateChange) {
        const newState = {
          values: stateRef.current?.values || {},
          touchedFields,
        };

        // preserve state update before render in case of sequential updates
        stateRef.current = newState;

        onStateChange(newState);
      }
    },
    [setInnerTouchedFields, onStateChange],
  );

  // side effects should be moved outside in case of multiple forms sharing state
  const handleOuterInputChange = useCallback(
    async (outerInput) => {
      let isNewValueRequested = false;

      const newValues = (
        await Promise.all(
          fields.map(async ({ field, getValueOnOuterInputChange }) => {
            if (!getValueOnOuterInputChange) {
              return;
            }

            isNewValueRequested = true;

            try {
              return [
                field,
                await getValueOnOuterInputChange({
                  outerInput,
                  getTouched: () => !!touchedFieldsRef.current?.[field],
                  getValue: () => valuesRef.current?.[field],
                }),
              ];
            } catch (error) {
              Logger.error(
                `Error while getting data for "${field}", error:`,
                fields.error,
              );
              // ignore this error
            }
          }),
        )
      ).reduce((acc, [field, value] = []) => {
        if (field) {
          acc[field] = value;
        }
        return acc;
      }, {});

      if (!isMounted() || !isNewValueRequested) {
        return;
      }

      handleValuesChanged({
        ...valuesRef.current,
        ...newValues,
      });
    },
    [fields, isMounted, handleValuesChanged],
  );

  const handleOuterInputChangeDebounced = useMemo(
    () => debounce(handleOuterInputChange, 300),
    [handleOuterInputChange],
  );

  useEffect(() => {
    handleOuterInputChangeDebounced(outerInput);
  }, [outerInput, handleOuterInputChangeDebounced]);

  const handleFieldChange = useCallback(
    (field, value, path) => {
      handleTouchedChanged({
        ...touchedFieldsRef.current,
        [field]: true,
      });
      // don't update handler every time value changes and get rid of unexpected effects
      handleValuesChanged({ ...valuesRef.current, [field]: value });
      setFieldsErrors((errors: Record<string, unknown> | null) => {
        const newErrors = { ...errors };

        const updateErrorAfterChange = fields.find(
          (item) => item.field === field,
        ).updateErrorAfterChange;
        if (updateErrorAfterChange) {
          newErrors[field] = updateErrorAfterChange(newErrors[field], path);
        } else {
          newErrors[field] = undefined;
        }

        return pickBy(newErrors, (val) => !isNil(val));
      });
    },
    [handleTouchedChanged, handleValuesChanged, fields],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      let fieldsErrors;

      fields.forEach(({ field, validate }) => {
        const error = validate && validate(values[field], values);

        if (error) {
          fieldsErrors = fieldsErrors
            ? {
                ...fieldsErrors,
                [field]: error,
              }
            : { [field]: error };
        }
      });

      if (fieldsErrors) {
        setFieldsErrors(fieldsErrors);
        return;
      }

      const formError = (validate && validate(values)) || null;
      if (formError) {
        setFormError(formError);
        return;
      }

      if (onSubmit) {
        onSubmit(values);
      }
    },
    [values, onSubmit, fields, validate],
  );

  const dirty = useMemo(
    () =>
      fields.reduce(
        (acc, { field, checkChanged }) =>
          acc ||
          (checkChanged
            ? checkChanged(values[field], initialValues[field], outerInput)
            : !isEqual(values[field], initialValues[field])),
        false,
      ),
    [fields, values, initialValues, outerInput],
  );

  return (
    <form className={classnames('form', className)} onSubmit={handleSubmit}>
      {formError && <Error className="form__error" text={formError} />}

      <div className="form__fields-container">
        {fields.map(
          ({
            field,
            type,
            hint,
            label,
            required,
            errorShownByControl,
            controlProps,
            beforeChange,
            getFormControlValue,
            hidden,
          }) => {
            if (hidden) {
              return null;
            }

            let value =
              values[field] !== null && values[field] !== undefined
                ? values[field]
                : initialValues[field];

            if (getFormControlValue) {
              value = getFormControlValue(values[field]);
            }

            return (
              <Field
                key={field}
                className={`form__field form__field-${kebabCase(field)}`}
                field={field}
                error={fieldsErrors && fieldsErrors[field]}
                type={type}
                hint={hint}
                label={label}
                required={required}
                errorShownByControl={errorShownByControl}
                controlProps={controlProps}
                value={value}
                beforeChange={beforeChange}
                onChange={handleFieldChange}
              />
            );
          },
        )}
      </div>

      {(onSubmit || onCancel || stepLabel) && (
        <div className="form__footer">
          {(onSubmit || onCancel) && (
            <>
              {footerHint && (
                <div className="form__footer-hint">{footerHint}</div>
              )}
              <div className="form__controls form__submit-container">
                {onSubmit && (
                  <Button
                    className="form__submit-button"
                    type="submit"
                    appearance="primary"
                    disabled={!dirty || formDisabled}
                  >
                    {submitLabel || 'Submit'}
                  </Button>
                )}
                {onCancel && (
                  <Button
                    className="form__cancel-button"
                    type="button"
                    onClick={onCancel}
                  >
                    {cancelLabel || 'Cancel'}
                  </Button>
                )}
                {stepLabel && <div className="form__step">{stepLabel}</div>}
              </div>
            </>
          )}
        </div>
      )}
    </form>
  );
};

export default Form;
